import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useTheme } from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
export const ICON_SCALE_DIVISOR = 30;
export const iconPathCache = new Map();
export const extractPathFromSvg = svgMarkup => {
  try {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgMarkup, 'image/svg+xml');
    const pathElements = svgDoc.querySelectorAll('path');
    if (pathElements.length > 0) {
      const allPaths = Array.from(pathElements).map(path => path.getAttribute('d') || '').filter(d => d.length > 0).map(pathData => {
        const trimmed = pathData.trim();
        if (trimmed && !trimmed.match(/^[Mm]/)) {
          return `M 0 0 ${trimmed}`;
        }
        return trimmed;
      }).join(' ');
      return allPaths;
    }
    return '';
  } catch (error) {
    return '';
  }
};
export const getIconPathData = (IconComponent, cacheKey, size) => {
  if (iconPathCache.has(cacheKey)) {
    return iconPathCache.get(cacheKey);
  }
  try {
    const svgMarkup = renderToStaticMarkup( /*#__PURE__*/createElement(IconComponent));
    const pathData = extractPathFromSvg(svgMarkup);
    iconPathCache.set(cacheKey, pathData);
    return pathData;
  } catch (error) {
    const fallbackSvg = `<svg viewBox="0 0 24 24" width="${size}" height="${size}"><path d="M12 4C7.58 4 4 7.58 4 12s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8z" fill="currentColor"/></svg>`;
    const fallbackPathData = extractPathFromSvg(fallbackSvg);
    iconPathCache.set(cacheKey, fallbackPathData);
    return fallbackPathData;
  }
};
const truncateTextIfNeeded = (context, text, labelWidth, maxWidth, UPSCALING_FACTOR, iconWithBufferWidth) => {
  if (!maxWidth) {
    return {
      text,
      width: labelWidth
    };
  }
  const maxLabelWidth = maxWidth * UPSCALING_FACTOR - iconWithBufferWidth;
  if (labelWidth <= maxLabelWidth) {
    return {
      text,
      width: labelWidth
    };
  }
  let truncatedText = text;
  let currentWidth = labelWidth;
  while (currentWidth > maxLabelWidth && truncatedText.length > 1) {
    truncatedText = truncatedText.slice(0, -1);
    const testText = `${truncatedText}...`;
    currentWidth = context.measureText(testText).width;
    if (currentWidth <= maxLabelWidth) {
      return {
        text: testText,
        width: currentWidth
      };
    }
  }
  return {
    text: truncatedText,
    width: currentWidth
  };
};
const renderTextToCanvas = (context, text, textColor, font, x, y, align) => {
  try {
    context.font = font;
  } catch (error) {
    console.error('Could not set font on context', error);
  }
  context.fillStyle = textColor;
  context.fillText(text, align(x), align(y));
};
export const renderIconToCanvas = (IconComponent, context, iconColor, iconSize, x, y, cacheKey) => {
  try {
    const pathData = getIconPathData(IconComponent, cacheKey, iconSize);
    if (pathData) {
      const path = new Path2D(pathData);
      context.save();
      context.translate(x, y);
      context.scale(iconSize / ICON_SCALE_DIVISOR, iconSize / ICON_SCALE_DIVISOR);
      context.fillStyle = iconColor;
      context.fill(path);
      context.restore();
    } else {
      context.fillStyle = iconColor;
      context.fillRect(x, y, iconSize, iconSize);
    }
  } catch (error) {
    context.fillStyle = iconColor;
    context.fillRect(x, y, iconSize, iconSize);
  }
};
export const TokenImage = ({
  IconComponent,
  text,
  iconColor = '#33475b',
  textColor = '#33475b',
  backgroundColor = '#ffffff',
  borderColor = '#cbd6e2',
  maxWidth,
  imgProps
}) => {
  const theme = useTheme();
  const UPSCALING_FACTOR = 4;
  const tokenHeight = 26 * UPSCALING_FACTOR;
  const textBaselineBuffer = 7 * UPSCALING_FACTOR;
  const tokenWidthBuffer = 16 * UPSCALING_FACTOR;
  const spaceAfterIcon = 8 * UPSCALING_FACTOR;
  const textSize = 16 * UPSCALING_FACTOR;
  const font = `${textSize}px ${theme.typography['body-100'].fontFamily}`;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return null;
  }
  const iconSize = textSize;
  const iconWidth = iconSize;
  const iconWithBufferWidth = iconWidth + spaceAfterIcon + tokenWidthBuffer;
  try {
    context.font = font;
  } catch (error) {
    console.error('Could not set font on context', error);
  }
  let labelWidth = context.measureText(text).width;
  let truncatedText = text;
  const truncationResult = truncateTextIfNeeded(context, text, labelWidth, maxWidth, UPSCALING_FACTOR, iconWithBufferWidth);
  truncatedText = truncationResult.text;
  labelWidth = truncationResult.width;
  const totalWidth = iconWithBufferWidth + labelWidth;
  const align = value => Math.floor(value / UPSCALING_FACTOR) * UPSCALING_FACTOR;
  canvas.setAttribute('width', `${align(totalWidth)}px`);
  canvas.setAttribute('height', `${align(tokenHeight)}px`);
  context.textAlign = 'left';
  const iconX = tokenWidthBuffer / 2;
  const iconY = (tokenHeight - iconSize) / 2;
  renderIconToCanvas(IconComponent, context, iconColor, iconSize, align(iconX), align(iconY), text);
  renderTextToCanvas(context, truncatedText, textColor, font, totalWidth - labelWidth - tokenWidthBuffer / 2, tokenHeight - textBaselineBuffer, align);
  const finalWidth = Math.floor(parseInt(canvas.getAttribute('width') || '', 10) / UPSCALING_FACTOR + 2);
  const finalHeight = Math.floor(parseInt(canvas.getAttribute('height') || '', 10) / UPSCALING_FACTOR + 2);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx("img", Object.assign({}, imgProps, {
      src: canvas.toDataURL(),
      alt: text,
      style: Object.assign({
        width: `${finalWidth}px`,
        height: `${finalHeight}px`,
        background: backgroundColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '2px',
        verticalAlign: 'middle',
        boxSizing: 'border-box'
      }, imgProps === null || imgProps === void 0 ? void 0 : imgProps.style),
      "data-truncated": String(truncatedText !== text)
    }))
  );
};